import type { LexicalCommand, LexicalEditor, RangeSelection, SerializedEditorState } from 'lexical';
import { createCommand } from 'lexical';

import type { LanguageEnum, Pronunciation } from 'js/src/client';

/* ACTION TYPES */
export const TRANSCRIPT_TYPES = {
    STORE_TRANSCRIPT: 'STORE_TRANSCRIPT',
    DELETE_TRANSCRIPT: 'DELETE_TRANSCRIPT',
    SET_TRANSCRIPT_TAB_VISIBILITY: 'SET_TRANSCRIPT_TAB_VISIBILITY',
    DISABLE_OVERLAYS: 'DISABLE_OVERLAYS',
    SCENE_CONVERSION_MODAL_VISIBLE: 'SCENE_CONVERSION_MODAL_VISIBLE',
    STORE_TRANSCRIPT_TRANSLATIONS: 'STORE_TRANSCRIPT_TRANSLATIONS',
    REMOVE_TRANSCRIPT_TRANSLATION: 'REMOVE_TRANSCRIPT_TRANSLATION',
    TOGGLE_ORIGINAL: 'TOGGLE_ORIGINAL',
    SET_TRANSCRIPT_LANGUAGE: 'SET_TRANSCRIPT_LANGUAGE',
    SET_CURRENT_TIME: 'SET_CURRENT_TIME',
    STORE_LEXICAL_EDITOR: 'STORE_LEXICAL_EDITOR',
    REMOVE_LEXICAL_EDITOR: 'REMOVE_LEXICAL_EDITOR',
    OVERWRITE_ALL_LEXICAL_EDITORS: 'OVERWRITE_ALL_LEXICAL_EDITORS',
    START_LEXICAL_CONTROLLING_ITS_OWN_STATE: 'START_LEXICAL_CONTROLLING_ITS_OWN_STATE',
    END_LEXICAL_CONTROLLING_ITS_OWN_STATE: 'END_LEXICAL_CONTROLLING_ITS_OWN_STATE',
    START_TIMING_MODE: 'START_TIMING_MODE',
    END_TIMING_MODE: 'END_TIMING_MODE',
};

// key = language code
export type TranscriptTranslations = Record<LanguageEnum, { editorState: SerializedEditorState }>;

/* TYPESCRIPT TYPES */
export type TranscriptData = {
    sceneGroupId: string;
    editorState: SerializedEditorState;
    isOriginal: boolean;
    disableOverlays: boolean;
    translations?: TranscriptTranslations;
    language?: LanguageEnum;
};

export type SerializedTranscript = {
    editorState: SerializedEditorState;
    disableOverlays: boolean;
    translations?: TranscriptTranslations;
    language?: LanguageEnum;
};

export type LexicalEditorsDataType = {
    sceneGroupId: string;
    editor: LexicalEditor;
    lexicalControlsItsOwnState: boolean;
};

export enum TypeOfTranscript {
    MEDIA_TRANSCRIPT_TAB = 'MEDIA_TRANSCRIPT_TAB',
    TRANSLATION = 'TRANSLATION',
    CLIP_CREATOR_MAIN = 'CLIP_CREATOR_MAIN',
    CLIP_CREATOR_CLIP = 'CLIP_CREATOR_CLIP',
}

export type ScrollTranscriptCommand = {
    sceneId: string;
};

export enum TranscriptRegionPurpose {
    ASSET_TIMING = 'asset-timing',
    ANIMATED_ZOOM = 'animated-zoom',
}

export enum TextCorrectionTrigger {
    BUTTON = 'button',
    DOUBLE_CLICK = 'double_click',
    CONTINUOUS = 'continuous',
}

export const VALIDATE_TRANSCRIPT_COMMAND: LexicalCommand<void> = createCommand();
export const START_CORRECT_TEXT_COMMAND: LexicalCommand<TextCorrectionTrigger> = createCommand();
export const SAVE_ACTIVE_CORRECTION_COMMAND: LexicalCommand<Event | null> = createCommand();
export const CANCEL_CORRECTION_COMMAND: LexicalCommand<null> = createCommand();
export const CORRECTION_SAVE_COMPLETE_COMMAND: LexicalCommand<null> = createCommand();
export const WORD_DRAGENTER_COMMAND: LexicalCommand<Event> = createCommand();
export const WORD_DRAGLEAVE_COMMAND: LexicalCommand<DragEvent> = createCommand();
export const HANDLE_DROP_COMMAND: LexicalCommand<Event> = createCommand();
export const CLEAR_SELECTION_COMMAND: LexicalCommand<null> = createCommand();
export const SELECTION_START_COMMAND: LexicalCommand<string | null> = createCommand();
export const SELECTION_END_COMMAND: LexicalCommand<Selection | RangeSelection | null> =
    createCommand();
export const SCROLL_TRANSCRIPT_COMMAND: LexicalCommand<ScrollTranscriptCommand> = createCommand();
export const REGENERATE_AI_VOICEOVER_COMMAND: LexicalCommand<void> = createCommand();
export const SPLIT_TRANSCRIPT_COMMAND: LexicalCommand<void> = createCommand();
export const EDIT_PRONUNCIATION: LexicalCommand<Pronunciation['target_word']> = createCommand();
export const ADD_PAUSE_START_COMMAND: LexicalCommand<void> = createCommand();
export const ADD_PAUSE_CHANGE_COMMAND: LexicalCommand<number> = createCommand();
export const ADD_PAUSE_END_COMMAND: LexicalCommand<boolean> = createCommand();
export const DELETE_PAUSE_COMMAND: LexicalCommand<void> = createCommand();
export const CUT_TRANSCRIPT_COMMAND: LexicalCommand<void> = createCommand();
export const UPDATE_TRANSCRIPT_COMMAND: LexicalCommand<void> = createCommand();
export const REMOVE_PAUSES_COMMAND: LexicalCommand<void> = createCommand();
export const REMOVE_FILLER_WORDS_COMMAND: LexicalCommand<void> = createCommand();
export const ENTER_TIMING_MODE_COMMAND: LexicalCommand<void> = createCommand();
export const EXIT_TIMING_MODE_COMMAND: LexicalCommand<void> = createCommand();

export const TRANSCRIPT_SCROLL_OPTIONS = Object.freeze({
    behavior: 'smooth',
    block: 'nearest',
});

export type SilenceNodeData = {
    startTime: number;
    endTime: number;
};

export enum InterestPointTimeType {
    AssetIntro = 'asset_intro',
    AssetOutro = 'asset_outro',
}
export type InterestPointTime = {
    timeType: InterestPointTimeType;
    sceneId: string;
    assetId: string;
    prevWordId: string | null; // null if before first word
    timeOffset: number; // milliseconds relative to prevWord.endTime
};
